/**
 * What part of the drawing is on screen — as millimetres, never as pixels.
 *
 * Kept in millimetres because a pixel offset means nothing once the scale
 * changes, and because the board's width is measured at run time: a view
 * stored in pixels would jump when somebody resized the window. A millimetre
 * point survives both.
 *
 * No React and no SVG in this file: the whole of the zoom arithmetic is
 * testable without rendering anything, and `PlanningBoard.tsx` is long enough
 * without it.
 */
import { MAX_SCALE, clampScale } from "./projection";

export interface Viewport {
  /** Pixels per millimetre, or `null` for „fit the whole shape". */
  scale: number | null;
  /** The millimetre point in the middle of the board, or `null` with a fit. */
  centreMm: { xMm: number; yMm: number } | null;
}

/**
 * „Alles zeigen" — and a state, not a number that happens to equal the fit.
 *
 * The same reason `tool: "select"` is a tool: a mode nobody can name is a mode
 * nobody can leave.
 */
export const FIT_VIEW: Viewport = { scale: null, centreMm: null };

/** Room left around a box that „Diesen Rost groß" fills, in pixels. */
const BOX_PADDING_PX = 40;

/**
 * Zoom by `factor` around `anchorMm`, which stays where it is on screen.
 *
 * `projectionCentreMm` is the millimetre point the current projection has in
 * the middle of the viewport (`Projection.centreMm`, `projection.ts`) — the
 * previous centre to hold on to when `view.centreMm` is itself `null`, i.e.
 * coming out of the fit view. Coming out of the fit, that middle is **the
 * shape's own centre**, not the anchor: an earlier version of this function
 * assumed there was no previous centre at all in that case and let the
 * anchor become the centre outright. That was wrong, and it is Befund 1
 * (walk-through, 10.09.2026) — the anchor drifted by exactly the distance
 * between itself and the shape's centre, in either direction depending on
 * which side of the centre it was on. There is no special case here anymore:
 * `view.centreMm ?? projectionCentreMm` supplies the one previous centre the
 * formula below needs, whichever view happened to supply it.
 */
export function zoomBy(
  view: Viewport,
  factor: number,
  anchorMm: { xMm: number; yMm: number },
  fitted: number,
  projectionCentreMm: { xMm: number; yMm: number },
): Viewport {
  const from = view.scale ?? fitted;
  const wanted = clampScale(from * factor, fitted);
  // `??` doesn't filter NaN, only null and undefined: if view.scale is NaN,
  // wanted will be NaN too. All NaN comparisons are false, so `<=` would
  // pass it through. `!(wanted > fitted)` catches both degenerate and fitted.
  if (!(wanted > fitted)) return FIT_VIEW;

  const centre = view.centreMm ?? projectionCentreMm;

  // Keeping the anchor still means moving the centre towards it by exactly the
  // share of the distance that the visible span loses: at twice the scale the
  // board shows half as much, so the centre travels half the way.
  const share = 1 - from / wanted;
  return {
    scale: wanted,
    centreMm: {
      xMm: centre.xMm + (anchorMm.xMm - centre.xMm) * share,
      yMm: centre.yMm + (anchorMm.yMm - centre.yMm) * share,
    },
  };
}

export interface BoxMm {
  minXMm: number;
  minYMm: number;
  maxXMm: number;
  maxYMm: number;
}

/**
 * The view that puts one box — a single element, say — on the whole board.
 *
 * „Diesen Rost groß" is this and nothing else: a scale and a centre, handed to
 * the same renderer as every other view (design D-5). There is no second
 * component, so there is no second place that draws grating.
 */
export function viewForBox(
  box: BoxMm,
  size: { width: number; height: number; padding?: number },
  fitted: number,
): Viewport {
  const padding = size.padding ?? BOX_PADDING_PX;
  const spanX = Math.max(box.maxXMm - box.minXMm, 0);
  const spanY = Math.max(box.maxYMm - box.minYMm, 0);
  const usableWidth = Math.max(size.width - 2 * padding, 1);
  const usableHeight = Math.max(size.height - 2 * padding, 1);

  // A box with no extent is a legitimate ask — a single point somebody wants
  // centred — and dividing by its span would be an Infinity that clamps to the
  // cap anyway. Say so instead of relying on that.
  const wanted =
    spanX === 0 && spanY === 0
      ? MAX_SCALE
      : Math.min(
          spanX === 0 ? Number.POSITIVE_INFINITY : usableWidth / spanX,
          spanY === 0 ? Number.POSITIVE_INFINITY : usableHeight / spanY,
        );

  return {
    scale: clampScale(wanted, fitted),
    centreMm: {
      xMm: box.minXMm + spanX / 2,
      yMm: box.minYMm + spanY / 2,
    },
  };
}
